import { AdvertisingEvent, PlayerEvent, type PlayerInterface } from '@foundation-player/loader';
import { readonly, writable, type Readable, type Writable } from 'svelte/store';
import type { PlayerStoreModule } from './player-store-types';

// We should not import from the foundation-ui. However, it would require a huge
// amount of work to separate the Foundation-UI parts from the PlayerState. So
// we keep it here.
// TODO: Separate shared and foundation-ui PlayerState.
import type { UIConfig } from '../../foundation-ui/types/ui-config';

/**
 * Readable store for the duration.
 *
 * Note: It does NOT handle the accumulated time of ad clips.
 * Use `adDuration` while `linearAdPlaying` is true.
 */
export class DurationPlayerStore implements PlayerStoreModule<'durationInSeconds'> {
  readonly #durationInSeconds: Writable<number> = writable(0);
  readonly durationInSeconds: Readable<number> = readonly(this.#durationInSeconds);

  constructor(uiConfig?: UIConfig) {
    if (typeof uiConfig?.durationInSeconds === 'number') {
      this.#durationInSeconds.set(uiConfig.durationInSeconds);
    }
  }

  initializeStateFromPlayer(player: PlayerInterface) {
    player.addEventListener(PlayerEvent.SessionStart, () => {
      this.#syncValueFromPlayer(player);
    });

    player.addEventListener(PlayerEvent.Ready, () => {
      this.#syncValueFromPlayer(player);
    });

    player.addEventListener(AdvertisingEvent.AdStarted, () => {
      this.#syncValueFromPlayer(player);
    });

    player.addEventListener(AdvertisingEvent.AdSlotCompleted, () => {
      const durationInSeconds = this.#syncValueFromPlayer(player);

      if (!durationInSeconds) {
        player.listenOnce(PlayerEvent.TimeChanged, () => {
          this.#syncValueFromPlayer(player);
        });
      }
    });

    player.addEventListener(PlayerEvent.ContentStart, () => {
      this.#syncValueFromPlayer(player);
    });

    this.#syncValueFromPlayer(player);
  }

  #syncValueFromPlayer(player: PlayerInterface): number {
    const durationInSeconds = player.getDuration();
    this.#durationInSeconds.set(durationInSeconds);

    return durationInSeconds;
  }
}
